import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  BeverageFormat,
  FarmBillStatus,
  validateProduct,
  type ProductInput,
  type FieldError,
  type Brand,
} from '@hygh-vybe/lib';
import { API_BASE } from '../../config';

interface ProductFormData extends ProductInput {
  description?: string;
  sponsored?: boolean;
  buyable?: boolean;
}

const EMPTY_FORM: ProductFormData = {
  brandName: '',
  productName: '',
  thcContentMg: undefined,
  beverageFormat: '',
  volumeOz: undefined,
  servingSize: '',
  sourceUrl: '',
  farmBillStatus: '',
  cbdContentMg: undefined,
  caloriesPerServing: undefined,
  sugarGrams: undefined,
  flavorProfile: '',
  priceUsd: undefined,
  imageUrl: '',
  onsetTimeMinutes: undefined,
  buyUrl: '',
  description: '',
  sponsored: false,
  buyable: false,
};

export function AdminProductForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { authFetch } = useAuth();

  const [form, setForm] = useState<ProductFormData>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [brandOptions, setBrandOptions] = useState<string[]>([]);

  // Fetch brands for dropdown
  useEffect(() => {
    async function fetchBrands() {
      try {
        const res = await authFetch(`${API_BASE}/v1/brands`);
        if (!res.ok) return;
        const json = await res.json();
        const names = (json.data || [])
          .map((b: Brand) => b.brandName)
          .filter(Boolean) as string[];
        names.sort((a, b) => a.localeCompare(b));
        setBrandOptions(names);
      } catch { /* non-critical */ }
    }
    fetchBrands();
  }, [authFetch]);

  // Load existing product for edit mode
  useEffect(() => {
    if (!isEdit || !id) return;

    async function loadProduct() {
      try {
        const res = await authFetch(`${API_BASE}/v1/products/${id}`);
        const json = await res.json();
        if (res.ok && json.data) {
          const p = json.data;
          setForm({
            brandName: p.brandName || '',
            productName: p.productName || '',
            thcContentMg: p.thcContentMg,
            beverageFormat: p.beverageFormat || '',
            volumeOz: p.volumeOz,
            servingSize: p.servingSize || '',
            sourceUrl: p.sourceUrl || '',
            farmBillStatus: p.farmBillStatus || '',
            cbdContentMg: p.cbdContentMg,
            caloriesPerServing: p.caloriesPerServing,
            sugarGrams: p.sugarGrams,
            flavorProfile: p.flavorProfile || '',
            priceUsd: p.priceUsd,
            imageUrl: p.imageUrl || '',
            onsetTimeMinutes: p.onsetTimeMinutes,
            buyUrl: p.buyUrl || '',
            description: p.description || '',
            sponsored: p.sponsored || false,
            buyable: p.buyable || false,
          });
        }
      } catch {
        setSubmitError('Failed to load product');
      } finally {
        setLoading(false);
      }
    }

    loadProduct();
  }, [id, isEdit, authFetch]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value === '' ? undefined : value,
    }));
    // Clear field error on change
    setFieldErrors((prev) => prev.filter((err) => err.field !== name));
  }

  function handleNumberChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value === '' ? undefined : parseFloat(value),
    }));
    setFieldErrors((prev) => prev.filter((err) => err.field !== name));
  }

  function getFieldError(field: string): string | undefined {
    const err = fieldErrors.find((e) => e.field === field);
    return err ? err.reason.replace(/_/g, ' ') : undefined;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(false);

    // Client-side validation
    const validation = validateProduct(form);
    if (!validation.success) {
      setFieldErrors(validation.errors);
      return;
    }

    setFieldErrors([]);
    setSubmitting(true);

    try {
      const url = isEdit
        ? `${API_BASE}/v1/products/${id}`
        : `${API_BASE}/v1/products`;

      const res = await authFetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      const json = await res.json();

      if (!res.ok) {
        if (json.error?.fields) {
          setFieldErrors(json.error.fields);
        }
        setSubmitError(json.error?.message || 'Failed to save product');
        return;
      }

      setSubmitSuccess(true);
      if (!isEdit) {
        // Redirect to products list after creation
        setTimeout(() => navigate('/admin/products'), 1000);
      }
    } catch {
      setSubmitError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <div className="admin-loading">Loading product...</div>;
  }

  return (
    <div>
      <h1>{isEdit ? 'Edit Product' : 'New Product'}</h1>

      {submitError && <div className="admin-alert admin-alert-error">{submitError}</div>}
      {submitSuccess && (
        <div className="admin-alert admin-alert-success">
          Product {isEdit ? 'updated' : 'created'} successfully!
        </div>
      )}

      <form className="admin-form" onSubmit={handleSubmit}>
        {/* Required Fields */}
        <div className="admin-form-row">
          <div className="admin-form-group">
            <label htmlFor="brandName">Brand Name *</label>
            <select
              id="brandName"
              name="brandName"
              value={form.brandName || ''}
              onChange={handleChange}
            >
              <option value="">Select brand...</option>
              {brandOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
            {getFieldError('brandName') && (
              <div className="field-error">{getFieldError('brandName')}</div>
            )}
          </div>
          <div className="admin-form-group">
            <label htmlFor="productName">Product Name *</label>
            <input
              id="productName"
              name="productName"
              value={form.productName || ''}
              onChange={handleChange}
              placeholder="e.g. Blood Orange Cardamom"
            />
            {getFieldError('productName') && (
              <div className="field-error">{getFieldError('productName')}</div>
            )}
          </div>
        </div>

        <div className="admin-form-row">
          <div className="admin-form-group">
            <label htmlFor="thcContentMg">THC Content (mg) *</label>
            <input
              id="thcContentMg"
              name="thcContentMg"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={form.thcContentMg ?? ''}
              onChange={handleNumberChange}
              placeholder="e.g. 5"
            />
            {getFieldError('thcContentMg') && (
              <div className="field-error">{getFieldError('thcContentMg')}</div>
            )}
          </div>
          <div className="admin-form-group">
            <label htmlFor="beverageFormat">Beverage Format *</label>
            <select
              id="beverageFormat"
              name="beverageFormat"
              value={form.beverageFormat || ''}
              onChange={handleChange}
            >
              <option value="">Select format...</option>
              {Object.values(BeverageFormat).map((fmt) => (
                <option key={fmt} value={fmt}>
                  {fmt.charAt(0).toUpperCase() + fmt.slice(1)}
                </option>
              ))}
            </select>
            {getFieldError('beverageFormat') && (
              <div className="field-error">{getFieldError('beverageFormat')}</div>
            )}
          </div>
        </div>

        <div className="admin-form-row">
          <div className="admin-form-group">
            <label htmlFor="volumeOz">Volume (oz) *</label>
            <input
              id="volumeOz"
              name="volumeOz"
              type="number"
              step="0.1"
              min="0.1"
              max="128"
              value={form.volumeOz ?? ''}
              onChange={handleNumberChange}
              placeholder="e.g. 12"
            />
            {getFieldError('volumeOz') && (
              <div className="field-error">{getFieldError('volumeOz')}</div>
            )}
          </div>
          <div className="admin-form-group">
            <label htmlFor="servingSize">Serving Size *</label>
            <input
              id="servingSize"
              name="servingSize"
              value={form.servingSize || ''}
              onChange={handleChange}
              placeholder="e.g. 1 can (12 oz)"
            />
            {getFieldError('servingSize') && (
              <div className="field-error">{getFieldError('servingSize')}</div>
            )}
          </div>
        </div>

        <div className="admin-form-group">
          <label htmlFor="sourceUrl">Source URL *</label>
          <input
            id="sourceUrl"
            name="sourceUrl"
            value={form.sourceUrl || ''}
            onChange={handleChange}
            placeholder="https://example.com/product-page"
          />
          {getFieldError('sourceUrl') && (
            <div className="field-error">{getFieldError('sourceUrl')}</div>
          )}
        </div>

        <div className="admin-form-group">
          <label htmlFor="buyUrl">Buy URL (product deep link for BUY NOW)</label>
          <input
            id="buyUrl"
            name="buyUrl"
            value={form.buyUrl || ''}
            onChange={handleChange}
            placeholder="https://brand-shop.com/products/this-drink"
          />
        </div>

        <div className="admin-form-group">
          <label htmlFor="farmBillStatus">Farm Bill Status *</label>
          <select
            id="farmBillStatus"
            name="farmBillStatus"
            value={form.farmBillStatus || ''}
            onChange={handleChange}
          >
            <option value="">Select status...</option>
            {Object.values(FarmBillStatus).map((status) => (
              <option key={status} value={status}>
                {status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              </option>
            ))}
          </select>
          {getFieldError('farmBillStatus') && (
            <div className="field-error">{getFieldError('farmBillStatus')}</div>
          )}
        </div>

        {/* Optional Fields */}
        <hr style={{ border: 'none', borderTop: '1px solid var(--color-border)', margin: '24px 0 16px' }} />

        <div className="admin-form-group">
          <label htmlFor="description">Product Description</label>
          <textarea
            id="description"
            name="description"
            value={(form as ProductFormData).description || ''}
            onChange={handleChange as React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>}
            placeholder="Detailed product description"
            rows={4}
            style={{ width: '100%', resize: 'vertical', padding: 'var(--spacing-sm)', fontSize: '0.875rem', border: '1px solid var(--color-border)', borderRadius: '6px', background: 'var(--color-bg)' }}
          />
        </div>

        <div className="admin-form-row">
          <div className="admin-form-group">
            <label htmlFor="buyable">Buyable (shows in Buy Now)</label>
            <select
              id="buyable"
              name="buyable"
              value={(form as ProductFormData).buyable ? 'true' : 'false'}
              onChange={(e) => setForm((prev) => ({ ...prev, buyable: e.target.value === 'true' }))}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </div>
          <div className="admin-form-group">
            <label htmlFor="sponsored">Sponsored</label>
            <select
              id="sponsored"
              name="sponsored"
              value={(form as ProductFormData).sponsored ? 'true' : 'false'}
              onChange={(e) => setForm((prev) => ({ ...prev, sponsored: e.target.value === 'true' }))}
            >
              <option value="false">No</option>
              <option value="true">Yes</option>
            </select>
          </div>
          <div className="admin-form-group">
            <label htmlFor="cbdContentMg">CBD Content (mg)</label>
            <input
              id="cbdContentMg"
              name="cbdContentMg"
              type="number"
              step="0.01"
              min="0"
              max="500"
              value={form.cbdContentMg ?? ''}
              onChange={handleNumberChange}
            />
            {getFieldError('cbdContentMg') && (
              <div className="field-error">{getFieldError('cbdContentMg')}</div>
            )}
          </div>
        </div>

        <div className="admin-form-row">
          <div className="admin-form-group">
            <label htmlFor="caloriesPerServing">Calories per Serving</label>
            <input
              id="caloriesPerServing"
              name="caloriesPerServing"
              type="number"
              step="1"
              min="0"
              max="500"
              value={form.caloriesPerServing ?? ''}
              onChange={handleNumberChange}
              placeholder="e.g. 5"
            />
          </div>
          <div className="admin-form-group">
            <label htmlFor="sugarGrams">Sugar (grams)</label>
            <input
              id="sugarGrams"
              name="sugarGrams"
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={form.sugarGrams ?? ''}
              onChange={handleNumberChange}
              placeholder="e.g. 0"
            />
          </div>
        </div>

        <div className="admin-form-row">
          <div className="admin-form-group">
            <label htmlFor="flavorProfile">Flavor Profile</label>
            <input
              id="flavorProfile"
              name="flavorProfile"
              value={form.flavorProfile || ''}
              onChange={handleChange}
              placeholder="e.g. Citrus, herbal"
            />
            {getFieldError('flavorProfile') && (
              <div className="field-error">{getFieldError('flavorProfile')}</div>
            )}
          </div>
          <div className="admin-form-group">
            <label htmlFor="priceUsd">Price (USD)</label>
            <input
              id="priceUsd"
              name="priceUsd"
              type="number"
              step="0.01"
              min="0.01"
              max="999.99"
              value={form.priceUsd ?? ''}
              onChange={handleNumberChange}
            />
            {getFieldError('priceUsd') && (
              <div className="field-error">{getFieldError('priceUsd')}</div>
            )}
          </div>
        </div>

        <div className="admin-form-row">
          <div className="admin-form-group">
            <label htmlFor="imageUrl">Image URL</label>
            <input
              id="imageUrl"
              name="imageUrl"
              value={form.imageUrl || ''}
              onChange={handleChange}
              placeholder="https://example.com/image.jpg"
            />
            {getFieldError('imageUrl') && (
              <div className="field-error">{getFieldError('imageUrl')}</div>
            )}
          </div>
          <div className="admin-form-group">
            <label htmlFor="onsetTimeMinutes">Onset Time (min)</label>
            <input
              id="onsetTimeMinutes"
              name="onsetTimeMinutes"
              type="number"
              min="5"
              max="120"
              value={form.onsetTimeMinutes ?? ''}
              onChange={handleNumberChange}
            />
            {getFieldError('onsetTimeMinutes') && (
              <div className="field-error">{getFieldError('onsetTimeMinutes')}</div>
            )}
          </div>
        </div>

        <div className="admin-form-actions">
          <button type="submit" className="admin-btn admin-btn-primary" disabled={submitting}>
            {submitting ? 'Saving...' : isEdit ? 'Update Product' : 'Create Product'}
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-secondary"
            onClick={() => navigate('/admin/products')}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
